import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlaybackContext, PlaybackState } from './playbackcontext';
import Reader from '../utils/reader/reader';

const STORAGE_KEYS = {
  volume: 'playback-volume',
  backgroundMusic: 'playback-bg-music',
  playbackRate: 'playback-rate',
};

function loadSavedPreferences(): Partial<PlaybackState> {
  if (typeof window === 'undefined') return {};
  try {
    const volume = window.localStorage.getItem(STORAGE_KEYS.volume);
    const bgMusic = window.localStorage.getItem(STORAGE_KEYS.backgroundMusic);
    const rate = window.localStorage.getItem(STORAGE_KEYS.playbackRate);
    return {
      ...(volume !== null ? { volume: parseFloat(volume) } : {}),
      ...(bgMusic !== null ? { backgroundMusicEnabled: bgMusic === 'true' } : {}),
      ...(rate !== null ? { playbackRate: parseFloat(rate) } : {}),
    };
  } catch { return {}; }
}

const defaultState: PlaybackState = {
  isPlaying: false,
  isPaused: false,
  currentIndex: 0,
  totalChunks: 0,
  currentSentence: '',
  scriptureTitle: '',
  scriptureSubtitle: '',
  playbackRate: 0.85,
  volume: 1,
  isMuted: false,
  backgroundMusicEnabled: true,
  visible: false,
};

export const PlaybackProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [state, setState] = useState<PlaybackState>(() => ({...defaultState, ...loadSavedPreferences()}));
  const readerRef = useRef<Reader | null>(null);
  const scrollCallbackRef = useRef<((index: number) => void) | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = loadSavedPreferences();
    readerRef.current = new Reader();

    // Apply saved preferences to reader
    if (saved.playbackRate !== undefined) readerRef.current.rate = saved.playbackRate;
    if (saved.backgroundMusicEnabled !== undefined) readerRef.current.backgroundMusicEnabled = saved.backgroundMusicEnabled;
    if (saved.volume !== undefined) readerRef.current.setVolume(saved.volume);

    readerRef.current.onIndexChange = (index, total, sentence) => {
      setState(prev => ({
        ...prev,
        currentIndex: index,
        totalChunks: total,
        currentSentence: sentence,
      }));
    };

    readerRef.current.onPlaybackEnd = () => {
      setState(prev => ({
        ...prev,
        isPlaying: false,
        isPaused: false,
        visible: false,
      }));
    };

    const handleRouteChange = () => {
      if (readerRef.current?.playing || readerRef.current?.paused) {
        readerRef.current.stop();
        setState(prev => ({...prev, isPlaying: false, isPaused: false, visible: false, currentIndex: 0, totalChunks: 0, currentSentence: '', scriptureTitle: '', scriptureSubtitle: ''}));
      }
    };

    window.addEventListener('gatsby-route-change', handleRouteChange);
    return () => {
      window.removeEventListener('gatsby-route-change', handleRouteChange);
      readerRef.current?.stop();
    };
  }, []);

  const play = useCallback((sentences: string[], lang: string, title: string, subtitle: string) => {
    if (!readerRef.current) return;
    setState(prev => ({
      ...prev,
      isPlaying: true,
      isPaused: false,
      visible: true,
      scriptureTitle: title,
      scriptureSubtitle: subtitle,
      totalChunks: sentences.length,
      currentIndex: 0,
      currentSentence: sentences[0] || '',
    }));
    readerRef.current.play(sentences, lang);
  }, []);

  const pause = useCallback(() => {
    readerRef.current?.pause();
    setState(prev => ({...prev, isPlaying: false, isPaused: true}));
  }, []);

  const resume = useCallback(() => {
    readerRef.current?.resume();
    setState(prev => ({...prev, isPlaying: true, isPaused: false}));
  }, []);

  const stop = useCallback(() => {
    readerRef.current?.stop();
    setState(prev => ({...prev, isPlaying: false, isPaused: false, visible: false, currentIndex: 0, totalChunks: 0, currentSentence: '', scriptureTitle: '', scriptureSubtitle: ''}));
  }, []);

  const nextChunk = useCallback(() => {
    if (!readerRef.current) return;
    const nextIdx = state.currentIndex + 1;
    if (nextIdx < state.totalChunks)
      readerRef.current.seekToIndex(nextIdx);
  }, [state.currentIndex, state.totalChunks]);

  const prevChunk = useCallback(() => {
    if (!readerRef.current) return;
    const prevIdx = state.currentIndex - 1;
    if (prevIdx >= 0)
      readerRef.current.seekToIndex(prevIdx);
  }, [state.currentIndex]);

  const seekToChunk = useCallback((index: number) => {
    if (!readerRef.current) return;
    if (index >= 0 && index < state.totalChunks)
      readerRef.current.seekToIndex(index);
  }, [state.totalChunks]);

  const setPlaybackRate = useCallback((rate: number) => {
    readerRef.current?.setRate(rate);
    window.localStorage.setItem(STORAGE_KEYS.playbackRate, rate.toString());
    setState(prev => ({...prev, playbackRate: rate}));
  }, []);

  const setVolume = useCallback((volume: number) => {
    readerRef.current?.setVolume(volume);
    window.localStorage.setItem(STORAGE_KEYS.volume, volume.toString());
    setState(prev => ({...prev, volume}));
  }, []);

  const toggleMute = useCallback(() => {
    const newMuted = !state.isMuted;
    if (newMuted) readerRef.current?.mute();
    else readerRef.current?.unmute();
    setState(prev => ({...prev, isMuted: newMuted}));
  }, [state.isMuted]);

  const toggleBackgroundMusic = useCallback(() => {
    readerRef.current?.toggleBackground();
    setState(prev => {
      const newVal = !prev.backgroundMusicEnabled;
      window.localStorage.setItem(STORAGE_KEYS.backgroundMusic, newVal.toString());
      return {...prev, backgroundMusicEnabled: newVal};
    });
  }, []);

  const dismiss = useCallback(() => {
    readerRef.current?.stop();
    setState(prev => ({...prev, isPlaying: false, isPaused: false, visible: false, currentIndex: 0, totalChunks: 0, currentSentence: '', scriptureTitle: '', scriptureSubtitle: ''}));
  }, []);

  const registerScrollCallback = useCallback((cb: ((index: number) => void) | null) => {
    scrollCallbackRef.current = cb;
  }, []);

  const scrollToCurrent = useCallback(() => {
    if (scrollCallbackRef.current && readerRef.current)
      scrollCallbackRef.current(readerRef.current.index);
  }, []);

  return (
    <PlaybackContext.Provider value={{
      state,
      reader: readerRef.current,
      play,
      pause,
      resume,
      stop,
      nextChunk,
      prevChunk,
      seekToChunk,
      setPlaybackRate,
      setVolume,
      toggleMute,
      toggleBackgroundMusic,
      dismiss,
      registerScrollCallback,
      scrollToCurrent,
    }}>
      {children}
    </PlaybackContext.Provider>
  );
};
